import React, {
	type ReactNode,
	type ReactElement,
	type FC,
	useCallback,
	memo,
	// eslint-disable-next-line jira/restricted/react-suspense
	Suspense,
	useId,
} from 'react';
import { useOwnershipMarkerComponent } from '@jira/platform__ownership-marker/src/ui/useOwnershipMarkerComponent.tsx';
import type { LazySuspenseProps } from '@atlassian/react-loosely-lazy';
import { getIsHydrationInProgress } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useHydrationSubtreeCompleter } from './useHydrationSubtreeCompleter.tsx';
import { useSwitchControl } from './useSwitchControl.tsx';
import { useHold } from './useHold.tsx';

const SuspenseWrapper: FC<{
	fallback: ReactNode;
	children: ReactNode;
	childrenRender: ReactElement;
	fallbackRender: ReactElement;
	unlocked?: boolean;
}> = ({ fallback, fallbackRender, children, childrenRender }) => (
	<Suspense
		fallback={
			<>
				{fallback}
				{fallbackRender}
			</>
		}
	>
		{children}
		{childrenRender}
	</Suspense>
);

/**
 * intermediate memo to avoid LazySuspense re-rendering when locked
 * also replaces useMemo for children and fallback "componsition"
 */
const MemoizedSuspense = memo(SuspenseWrapper, (prev, next) => {
	if (!next.unlocked) {
		return true;
	}
	return next.fallback === prev.fallback && next.children === prev.children;
});

/**
 * This is a "safe-to-use" Placeholder but it's actually expected to be used only around React-Loosely-Lazy components.
 * WARNING: Make sure we use same placeholders in a rendering tree.
 * Mixed use may cause unexpected behavior, such as rendering child component twice.
 * Ref: https://hello.atlassian.net/wiki/x/iJpOHwE
 *
 * @deprecated - do not use file directly (yet)
 */
export const SuspensePlaceholder: FC<{
	/**
	 * Named Placeholders will help when analysing the break-down of the page load in Performance Portal.
	 * */
	name: string;
	children?: LazySuspenseProps['children'];
	fallback?: LazySuspenseProps['fallback'];
}> = ({ name, children, fallback }) => {
	const [renderedPath, renderedPathRef, render] = useSwitchControl();

	const isHoldEnabled = useCallback(
		() => renderedPathRef.current !== 'children',
		[renderedPathRef],
	);

	useHold(
		name,
		// hold is "controlled" by the fact of children render
		renderedPath !== 'children',
		// if children become visible we don't need to hold anymore
		isHoldEnabled,
	);
	const isContentRendered = Boolean(renderedPath);
	useHydrationSubtreeCompleter({ isSubtreeRendered: isContentRendered, name });

	const OwnershipMarker = useOwnershipMarkerComponent({ markerType: 'placeholder', name });
	return (
		<OwnershipMarker>
			{/* We add hydration markers before and after the boundary to detect divergence in React fibers */}
			{fg('jfp-magma-placeholder-hydration-markers') && <HydrationMarker />}
			<MemoizedSuspense
				fallback={fallback}
				fallbackRender={render.fallback}
				childrenRender={render.children}
				unlocked={isContentRendered || !getIsHydrationInProgress()}
			>
				{children}
			</MemoizedSuspense>
			{fg('jfp-magma-placeholder-hydration-markers') && <HydrationMarker />}
		</OwnershipMarker>
	);
};

/**
 * This component can be use to see if there has been divergence in the react fibers between the server and client.
 * If there is, we can expect to see different values for `data-hydration-marker` between CSR and SSR.
 */
function HydrationMarker() {
	const uid = useId();
	return <input type="hidden" data-hydration-marker={uid} />;
}
